import type { IconName } from '@customerio/pluma-icons';
import { defaultFilter } from 'cmdk';
import type { LocalCommand } from './local-commands';
import type {
  SearchResult,
  TemplateType,
  CustomerResult,
  SourceResult,
  DestinationResult,
  AdAudienceResult,
  AdAudienceCredentialType,
  CustomFormResult,
  ReportingWebhookResult,
  CdpOutResult,
  DataOutResult,
  DataOutExporterType,
  ObjectTypeResult,
} from '@/services/auth/types';
import type {
  ServicesApiCustomer,
  ServicesApiPipelinesConnection,
  ServicesApiAudienceCredential,
  ServicesApiCustomForm,
  ServicesApiReportingWebhook,
  ServicesApiCdpOutIntegration,
  ServicesApiFacebookLeadAdsForm,
  ServicesApiDataOutIntegration,
  ServicesApiMParticleIntegration,
  ServicesApiSegmentOutIntegration,
  ServicesApiObjectType,
} from '@/services/api/services-api';
import { resolveAssetUrl } from '@/utils/resolve-asset-url';
import {
  ACTION_TYPE_ICONS,
  CAMPAIGN_TYPE_ICONS,
  CUSTOM_FORM_TYPE_IMAGE_FILENAMES,
  CUSTOM_FORM_TYPE_LABELS,
  CUSTOM_FORM_TYPE_PLUMA_ICONS,
  FACEBOOK_LEAD_ADS_FORM_TYPE,
  TEMPLATE_TYPE_ICONS,
  type GroupType,
} from './constants';
import { isEmptyString } from '@/utils/string';

type CatalogSourceEntry = { option_id: string; name: string; icon: string; category: string };
type CatalogDestinationEntry = CatalogSourceEntry & { mode?: string };

export type IntegrationCatalog = Map<CatalogDestinationEntry['option_id'], Omit<CatalogDestinationEntry, 'option_id'>>;

export function buildIntegrationCatalog(
  sources: CatalogSourceEntry[],
  destinations: CatalogDestinationEntry[],
): IntegrationCatalog {
  const map: IntegrationCatalog = new Map();
  for (const opt of sources) {
    map.set(opt.option_id, { name: opt.name, icon: opt.icon, category: opt.category });
  }
  for (const opt of destinations) {
    map.set(opt.option_id, { name: opt.name, icon: opt.icon, category: opt.category, mode: opt.mode });
  }
  return map;
}

export function getIntegrationTypeName(type: string, catalog: IntegrationCatalog): string | undefined {
  return catalog.get(type)?.name;
}

function getIntegrationIconUrl(type: string, catalog: IntegrationCatalog): string | undefined {
  const entry = catalog.get(type);
  return entry != null ? resolveAssetUrl(entry.icon) : undefined;
}

/**
 * Fuzzy-matches connections against the query by title, type slug, and catalog display name,
 * then caps the result set. Capping after filtering avoids hiding later-indexed connections
 * (e.g. destinations on workspaces with many sources, since the API returns connections
 * grouped by direction).
 */
export function filterAndCapIntegrations(
  connections: readonly (SourceResult | DestinationResult)[],
  query: string,
  catalog: IntegrationCatalog,
  cap: number,
): (SourceResult | DestinationResult)[] {
  if (query.length === 0) return [];
  const q = query.toLowerCase();
  const matches: (SourceResult | DestinationResult)[] = [];
  for (const r of connections) {
    const displayName = getIntegrationTypeName(r.other.type, catalog);
    if ([r.title, r.other.type, displayName ?? ''].some((s) => defaultFilter(s.toLowerCase(), q) > 0)) {
      matches.push(r);
      if (matches.length >= cap) break;
    }
  }
  return matches;
}

/**
 * Maps a ServicesApiCustomer to a CustomerResult for use in the command dialog.
 *
 * Title resolution order: name → email → "Person with id {id}" → "cio_id: {cio_id}"
 *
 * The `_created_in_customerio_at` attribute is a Unix timestamp (seconds) stored as a string.
 * It is converted to an ISO 8601 string for display; invalid or missing values are omitted.
 */
export function mapCustomerToSearchResult(customer: ServicesApiCustomer, workspaceId: number): CustomerResult {
  const cioCreatedAt = customer.attributes?.['_created_in_customerio_at'] as string | undefined;
  const parsedCreatedAt =
    cioCreatedAt != null && !isEmptyString(cioCreatedAt) ? Number.parseInt(cioCreatedAt, 10) : Number.NaN;
  const formattedCreatedAt = !Number.isNaN(parsedCreatedAt)
    ? new Date(parsedCreatedAt * 1000).toISOString()
    : undefined;

  const name = customer.attributes['name'];
  const email = customer.attributes['email'];
  const nameString = typeof name === 'string' && !isEmptyString(name) ? name : undefined;
  const emailString = typeof email === 'string' && !isEmptyString(email) ? email : undefined;
  const title =
    nameString ??
    emailString ??
    (customer.id !== undefined ? `Person with id ${customer.id}` : `cio_id: ${customer.cio_id}`);

  return {
    kind: 'customer',
    doc_id: customer.id ?? customer.cio_id,
    title,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/people/${customer.id ?? customer.cio_id}`,
    other: {
      id: customer.id,
      email: emailString,
      unsubscribed: customer.unsubscribed,
      created_at: formattedCreatedAt,
    },
  };
}

/**
 * Maps a ServicesApiPipelinesConnection to a SourceResult or DestinationResult.
 * The `path` field from the API is used as the navigation URL.
 */
export function mapPipelinesConnectionToSearchResult(
  conn: ServicesApiPipelinesConnection,
): SourceResult | DestinationResult {
  const other = {
    type: conn.type,
    enabled: conn.enabled,
    status: conn.status,
    created_at: conn.created_at > 0 ? conn.created_at * 1000 : undefined,
    last_activity: conn.last_activity > 0 ? conn.last_activity * 1000 : undefined,
  };
  return conn.direction === 'Source'
    ? { kind: 'source', doc_id: conn.id, title: conn.name, url: conn.path, other }
    : { kind: 'destination', doc_id: conn.id, title: conn.name, url: conn.path, other };
}

const AD_AUDIENCE_DISPLAY_NAMES: Record<AdAudienceCredentialType, string> = {
  facebook: 'Facebook Ads',
  adwords: 'Google Ads',
};

const AD_AUDIENCE_SEARCH_ALIASES: Record<AdAudienceCredentialType, string[]> = {
  facebook: ['facebook ads', 'facebook', 'meta ads'],
  adwords: ['google ads', 'google', 'adwords'],
};

/**
 * Maps a ServicesApiAudienceCredential to an AdAudienceResult.
 * Credentials are surfaced as the search target (shape decision: option a).
 * The URL links to the integrations audiences settings page for the credential type.
 */
export function mapAudienceCredentialToSearchResult(
  credential: ServicesApiAudienceCredential,
  workspaceId: number,
): AdAudienceResult {
  const credType = credential.type as AdAudienceCredentialType;
  return {
    kind: 'ad_audience',
    doc_id: `ad_audience-${credential.id}`,
    title: AD_AUDIENCE_DISPLAY_NAMES[credType] ?? credential.type,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/integrations/audiences/${credential.type}/settings`,
    other: {
      credential_type: credType,
      expired: credential.expired,
      created_at: credential.created_at > 0 ? credential.created_at * 1000 : undefined,
    },
  };
}

/**
 * Client-side filter for ad audience credentials. Matches against display name and type aliases.
 */
export function filterAudienceCredentials(
  credentials: ServicesApiAudienceCredential[],
  query: string,
): ServicesApiAudienceCredential[] {
  if (isEmptyString(query)) return [];
  const q = query.toLowerCase();
  return credentials.filter((c) => {
    const aliases = AD_AUDIENCE_SEARCH_ALIASES[c.type as AdAudienceCredentialType] ?? [c.type];
    return aliases.some((alias) => defaultFilter(alias, q) > 0);
  });
}

/**
 * Maps a ServicesApiCustomForm to a CustomFormResult.
 * The edit URL follows the Journeys integrations/forms/:type/:id route.
 */
export function mapCustomFormToSearchResult(form: ServicesApiCustomForm, workspaceId: number): CustomFormResult {
  return {
    kind: 'custom_form',
    doc_id: `custom_form-${form.id}`,
    title: form.name,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/integrations/forms/${form.form_type}/${form.id}`,
    other: {
      env_id: workspaceId,
      form_type: form.form_type,
      form_id: form.form_id,
      status: form.status,
      created_at: form.created_at > 0 ? form.created_at * 1000 : undefined,
      last_activity_at: form.last_activity_at > 0 ? form.last_activity_at * 1000 : undefined,
      html_form_url: form.html_form_url.length > 0 ? form.html_form_url : undefined,
    },
  };
}

export function mapFacebookLeadAdsFormToSearchResult(
  form: ServicesApiFacebookLeadAdsForm,
  workspaceId: number,
): CustomFormResult {
  return {
    kind: 'custom_form',
    doc_id: `${FACEBOOK_LEAD_ADS_FORM_TYPE}_form-${form.id}`,
    title: form.facebook_form_name,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/integrations/forms/${FACEBOOK_LEAD_ADS_FORM_TYPE}/${form.id}`,
    other: {
      env_id: workspaceId,
      form_type: FACEBOOK_LEAD_ADS_FORM_TYPE,
      form_id: form.facebook_form_id,
      status: form.status,
      created_at: form.created_at > 0 ? form.created_at * 1000 : undefined,
      last_activity_at: form.last_activity_at > 0 ? form.last_activity_at * 1000 : undefined,
    },
  };
}

/**
 * Maps a ServicesApiReportingWebhook to a ReportingWebhookResult.
 * The edit URL follows the Journeys integrations/reporting-webhooks/:id route.
 */
export function mapReportingWebhookToSearchResult(
  webhook: ServicesApiReportingWebhook,
  workspaceId: number,
): ReportingWebhookResult {
  return {
    kind: 'reporting_webhook',
    doc_id: `reporting_webhook-${webhook.id}`,
    title: webhook.name,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/integrations/reporting-webhooks/${webhook.id}`,
    other: {
      enabled: webhook.enabled,
      created_at: webhook.created_at > 0 ? webhook.created_at * 1000 : undefined,
    },
  };
}

/**
 * Client-side filter for Facebook Lead Ads forms. Matches against name, form type slug, and configured label.
 */
export function filterFacebookLeadAdsForms(
  forms: ServicesApiFacebookLeadAdsForm[],
  query: string,
  cap: number,
): ServicesApiFacebookLeadAdsForm[] {
  if (isEmptyString(query)) return [];
  const q = query.toLowerCase();
  const matches: ServicesApiFacebookLeadAdsForm[] = [];
  const displayLabel = CUSTOM_FORM_TYPE_LABELS[FACEBOOK_LEAD_ADS_FORM_TYPE];
  for (const f of forms) {
    if (
      [FACEBOOK_LEAD_ADS_FORM_TYPE, displayLabel, f.facebook_form_name].some(
        (s) => defaultFilter(s.toLowerCase(), q) > 0,
      )
    ) {
      matches.push(f);
      if (matches.length >= cap) break;
    }
  }
  return matches;
}

/**
 * Client-side filter for custom forms. Matches against name, form_type slug, and configured label.
 */
export function filterCustomForms(forms: ServicesApiCustomForm[], query: string, cap: number): ServicesApiCustomForm[] {
  if (isEmptyString(query)) return [];
  const q = query.toLowerCase();
  const matches: ServicesApiCustomForm[] = [];
  for (const f of forms) {
    const displayLabel = CUSTOM_FORM_TYPE_LABELS[f.form_type] ?? '';
    if ([f.name, f.form_type, displayLabel].some((s) => defaultFilter(s.toLowerCase(), q) > 0)) {
      matches.push(f);
      if (matches.length >= cap) break;
    }
  }
  return matches;
}

/**
 * Client-side filter for reporting webhooks. Matches against name.
 */
export function filterReportingWebhooks(
  webhooks: ServicesApiReportingWebhook[],
  query: string,
  cap: number,
): ServicesApiReportingWebhook[] {
  if (isEmptyString(query)) return [];
  const q = query.toLowerCase();
  const matches: ServicesApiReportingWebhook[] = [];
  for (const webhook of webhooks) {
    if (defaultFilter(webhook.name.toLowerCase(), q) > 0) {
      matches.push(webhook);
      if (matches.length >= cap) break;
    }
  }
  return matches;
}

/**
 * Maps a ServicesApiCdpOutIntegration to a CdpOutResult.
 * CDP Out is a singleton per workspace; its fixed display name is "CDP Event Forwarding".
 * The URL points to the Journeys cdp_out settings page.
 *
 * The query is matched against the name "CDP Event Forwarding" and common synonyms
 * ("CDP", "forwarding", "segment") so users can find it without knowing the exact label.
 */
export function mapCdpOutToSearchResult(cdpOut: ServicesApiCdpOutIntegration, workspaceId: number): CdpOutResult {
  return {
    kind: 'cdp_out',
    doc_id: `cdp_out-${cdpOut.id}`,
    title: 'CDP Event Forwarding',
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/integrations/cdp_out`,
    other: {
      enabled: cdpOut.enabled,
      created_at: cdpOut.created_at > 0 ? cdpOut.created_at * 1000 : undefined,
    },
  };
}

const CDP_OUT_SEARCH_TERMS = ['cdp event forwarding', 'cdp', 'forwarding', 'segment out', 'event forwarding'];

/**
 * Returns whether the query matches the CDP Out integration.
 * CDP Out is a singleton, so this is a simple substring/fuzzy check against fixed terms.
 */
export function matchesCdpOut(query: string): boolean {
  if (isEmptyString(query)) return false;
  const q = query.toLowerCase();
  return CDP_OUT_SEARCH_TERMS.some((term) => defaultFilter(term, q) > 0);
}

/** Navigation URL per data-out exporter type. */
const DATA_OUT_URLS: Record<DataOutExporterType, (workspaceId: number) => string> = {
  mixpanel: (id) => `${window.location.origin}/workspaces/${id}/journeys/integrations/data_out/mixpanel`,
  amplitude: (id) => `${window.location.origin}/workspaces/${id}/journeys/integrations/data_out/amplitude`,
  segment_out: (id) => `${window.location.origin}/workspaces/${id}/journeys/integrations/segment_out`,
  mparticle: (id) => `${window.location.origin}/workspaces/${id}/journeys/integrations/mparticle_out`,
};

const DATA_OUT_DISPLAY_NAMES: Record<DataOutExporterType, string> = {
  mixpanel: 'Mixpanel',
  amplitude: 'Amplitude',
  segment_out: 'Segment',
  mparticle: 'mParticle',
};

const DATA_OUT_SEARCH_ALIASES: Record<DataOutExporterType, string[]> = {
  mixpanel: ['mixpanel'],
  amplitude: ['amplitude'],
  segment_out: ['segment', 'segment out', 'segment_out'],
  mparticle: ['mparticle', 'm particle'],
};

function makeDataOutResult(
  exporterType: DataOutExporterType,
  id: number,
  enabled: boolean,
  createdAt: number,
  workspaceId: number,
): DataOutResult {
  return {
    kind: 'data_out',
    doc_id: `data_out-${exporterType}-${id}`,
    title: DATA_OUT_DISPLAY_NAMES[exporterType],
    url: DATA_OUT_URLS[exporterType](workspaceId),
    other: {
      exporter_type: exporterType,
      enabled,
      created_at: createdAt > 0 ? createdAt * 1000 : undefined,
    },
  };
}

export function mapDataOutToSearchResult(
  integration: ServicesApiDataOutIntegration,
  workspaceId: number,
): DataOutResult {
  return makeDataOutResult(
    integration.identifier as DataOutExporterType,
    integration.id,
    integration.enabled,
    integration.created_at,
    workspaceId,
  );
}

export function mapMParticleToSearchResult(
  integration: ServicesApiMParticleIntegration,
  workspaceId: number,
): DataOutResult {
  return makeDataOutResult('mparticle', integration.id, integration.enabled, integration.created_at, workspaceId);
}

export function mapSegmentOutToSearchResult(
  integration: ServicesApiSegmentOutIntegration,
  workspaceId: number,
): DataOutResult {
  return makeDataOutResult('segment_out', integration.id, integration.enabled, integration.created_at, workspaceId);
}

/**
 * Client-side filter for data-out integrations. Matches against display name, exporter slug, and aliases.
 */
export function filterDataOutResults(results: DataOutResult[], query: string): DataOutResult[] {
  if (isEmptyString(query)) return [];
  const q = query.toLowerCase();
  return results.filter((result) => {
    const aliases = DATA_OUT_SEARCH_ALIASES[result.other.exporter_type];
    return [result.title, result.other.exporter_type, ...aliases].some(
      (term) => defaultFilter(term.toLowerCase(), q) > 0,
    );
  });
}

/**
 * Client-side filter for object types. Matches against name, singular_name, and slug.
 * Only returns enabled types.
 */
export function filterObjectTypes(
  objectTypes: ServicesApiObjectType[],
  query: string,
  cap: number,
): ServicesApiObjectType[] {
  if (isEmptyString(query)) return [];
  const q = query.toLowerCase();
  const matches: ServicesApiObjectType[] = [];
  for (const t of objectTypes) {
    if (!t.enabled) continue;
    if ([t.name, t.singular_name, t.slug].some((s) => defaultFilter(s.toLowerCase(), q) > 0)) {
      matches.push(t);
      if (matches.length >= cap) break;
    }
  }
  return matches;
}

/**
 * Maps a ServicesApiObjectType to an ObjectTypeResult.
 * Links to the objects list page for the given slug.
 */
export function mapObjectTypeToSearchResult(objectType: ServicesApiObjectType, workspaceId: number): ObjectTypeResult {
  return {
    kind: 'object_type',
    doc_id: `object_type-${objectType.id}`,
    title: objectType.name,
    url: `${window.location.origin}/workspaces/${workspaceId}/journeys/objects/${objectType.slug}`,
    other: {
      id: objectType.id,
      env_id: workspaceId,
      slug: objectType.slug,
      singular_name: objectType.singular_name,
      object_count: objectType.object_count,
      icon: objectType.icon,
    },
  };
}

/**
 * Builds the query string for the people (customers) endpoint.
 * The endpoint supports wildcard searches — if the input already contains '@' (email/domain)
 * or '*' (explicit wildcard), pass it through unchanged.
 * Otherwise append '*' to treat plain text as a prefix match (e.g. "john" → "john*").
 *
 * An empty string intentionally returns '*' (match all customers), which is the correct
 * API behavior for a "show all people" mode. The query is currently gated by
 * isSearchingEnabled (requires non-empty input), so this path is not yet reachable —
 * but '*' is the right value if that gate is ever relaxed.
 */
export function buildPeopleSearchQuery(query: string): string {
  if (query.includes('@') || query.includes('*')) return query;
  return `${query}*`;
}

/**
 * Computes the 1-based position of an item within the command dialog result list.
 *
 * Layout order:
 *   [quick actions]   → 1 + cmdIndex
 *   [local search]    → 1 + hydraCommandsCount + groupOffset + resultIndex
 *   [api results]     → 1 + hydraCommandsCount + localSearchCount + groupOffset + index
 *   [navigation]      → 1 + hydraCommandsCount + localSearchCount + apiResultsCount + index
 *   [ask agent]       → always last
 */
export function computeItemPosition({
  hydraCommandsCount,
  localSearchCount,
  section,
  groupOffset = 0,
  itemIndex,
}: {
  hydraCommandsCount: number;
  localSearchCount: number;
  section: 'localSearch' | 'apiResults';
  groupOffset?: number;
  itemIndex: number;
}): number {
  switch (section) {
    case 'localSearch':
      return 1 + hydraCommandsCount + groupOffset + itemIndex;
    case 'apiResults':
      return 1 + hydraCommandsCount + localSearchCount + groupOffset + itemIndex;
    default: {
      const _exhaustive: never = section;
      return _exhaustive;
    }
  }
}

// Covers all navigation entries currently indexed by Vertex (Discovery Engine).
// Will be replaced when navigation results move to FE-generated local data.
// Ordered most-specific first so the first match wins.
const NAVIGATION_ICON_PATTERNS: [string, IconName][] = [
  // Broadcasts (specific sub-routes before generic)
  ['broadcasts/api-triggered', 'api-triggered-broadcast'],
  ['broadcasts/newsletter', 'newsletter'],
  ['/broadcasts', 'broadcast'],
  // Segments
  ['segments/explore', 'segment-data-driven'],
  ['/segments', 'segment'],
  // Messaging
  ['/campaigns', 'campaigns'],
  ['/transactional', 'transactional-api'],
  ['/anonymous', 'anonymous'],
  ['/outbox', 'delivery-log'],
  // Audience
  ['/people', 'person'],
  ['/objects', 'object'],
  ['/goals', 'goal'],
  // Review
  ['/activity_logs', 'activity'],
  ['/analysis', 'analysis'],
  ['/health', 'analysis'],
  // Content
  ['message-library', 'folder'],
  ['/assets', 'image'],
  ['/layouts', 'layout'],
  ['/snippets', 'liquid'],
  // Data
  ['data-index', 'data-index'],
  ['integrations/reporting-webhooks', 'webhook'],
  ['/integrations', 'integrations'],
  ['/imports', 'import'],
  ['/exports', 'export'],
  ['/collections', 'database'],
  // Design Studio
  ['design-studio', 'design-studio-new'],
  // Agent / AI
  ['/agent', 'sparkle'],
  // Pipelines
  ['/pipelines', 'data-index'],
  // Settings – actions (channel-specific before generic)
  ['actions/email', 'email'],
  ['actions/push', 'mobile'],
  ['actions/in_app', 'in-app'],
  ['actions/slack', 'slack'],
  ['actions/sms', 'sms'],
  ['actions/twilio', 'sms'],
  ['actions/whatsapp', 'brand/whatsapp'],
  ['actions/messaging_limit', 'message-limit'],
  ['actions/time_zone_match', 'time-window'],
  ['actions/url_params', 'link'],
  // Settings – workspace-specific
  ['settings/ai-business-profile', 'sparkle'],
  ['settings/llm_configuration', 'sparkle'],
  ['settings/api_credentials', 'api-credentials'],
  ['settings/api-keys', 'api-credentials'],
  ['settings/webhook_configuration', 'webhook'],
  ['settings/subscription_center', 'email'],
  ['settings/merge_options', 'merge'],
  ['settings/suppressions', 'blocks'],
  ['settings/audit-logs', 'audit'],
  // Settings – account-level
  ['/settings/security', 'lock-secure'],
  ['/settings/team', 'manage-team'],
  ['/settings/roles', 'manage-team'],
  ['/settings/billing', 'billing-history'],
  ['/settings/privacy', 'data-privacy'],
  ['/settings/personal', 'person'],
  ['/settings/workspaces', 'workspaces'],
  ['/settings/experimental-features', 'sparkle'],
  ['/settings/account', 'workspace-settings'],
  // Home / Dashboard (after more-specific paths that also contain "home" or "dashboard")
  ['/home', 'home'],
  ['/dashboard', 'home'],
  // Generic settings fallback
  ['/settings', 'workspace-settings'],
];

function getNavigationIcon(url: string): IconName {
  const path = URL.canParse(url) ? new URL(url).pathname : url;
  for (const [pattern, icon] of NAVIGATION_ICON_PATTERNS) {
    if (path.includes(pattern)) return icon;
  }
  return 'arrow-right';
}

export function getIconNameFromSearchResult(result: SearchResult): IconName {
  switch (result.kind) {
    case 'campaign':
      return CAMPAIGN_TYPE_ICONS?.[result.other.campaign_type] ?? 'campaigns';
    case 'template':
      return TEMPLATE_TYPE_ICONS?.[result.other.template_type] ?? 'layout';
    case 'segment':
      return result.other.segment_type === 'static' ? 'segment-manual' : 'segment-data-driven';
    case 'action':
      return ACTION_TYPE_ICONS?.[result.other.action_type] ?? 'help';
    case 'newsletter':
      return 'newsletter';
    case 'goal':
      return 'goal';
    case 'navigation':
      return getNavigationIcon(result.url);
    case 'customer':
      return 'person';
    case 'docs':
      return 'connect-file';
    case 'source':
      return 'data-index';
    case 'destination':
      return 'integrations';
    case 'ad_audience':
      return 'targeting';
    case 'custom_form':
      return CUSTOM_FORM_TYPE_PLUMA_ICONS[result.other.form_type] ?? 'form';
    case 'reporting_webhook':
      return 'webhook';
    case 'cdp_out':
      return 'data-index';
    case 'data_out':
      return 'export';
    case 'object_type':
      return (result.other.icon != null && result.other.icon !== '' ? result.other.icon : 'object') as IconName;
    default:
      return 'search';
  }
}

export function getFormTypeImageUrl(formType: string): string | undefined {
  const filename = CUSTOM_FORM_TYPE_IMAGE_FILENAMES[formType];
  return filename != null ? resolveAssetUrl(`hydra-assets/icons/${filename}`) : undefined;
}

export function getResultImageUrl(result: SearchResult, catalog: IntegrationCatalog): string | undefined {
  if (result.kind === 'source' || result.kind === 'destination') {
    return getIntegrationIconUrl(result.other.type, catalog);
  }
  if (result.kind === 'custom_form') {
    return getFormTypeImageUrl(result.other.form_type);
  }
  return undefined;
}

function groupTemplatesByType(results: SearchResult[]) {
  const templateTypes: TemplateType[] = [];

  const groupedByTemplateType = results.reduce(
    (groups, item) => {
      if (item.kind !== 'template') {
        return groups;
      }

      const templateType = item.other.template_type;

      if (groups[templateType] == null) {
        templateTypes.push(templateType);
        groups[templateType] = [];
      }

      groups[templateType].push(item);

      return groups;
    },
    {} as Record<TemplateType, SearchResult[]>,
  );

  return { groupedByTemplateType, templateTypes };
}

/**
 * The backend skips indexing templates with template_type "unset", so in practice
 * this remap should never fire for newly indexed content. It exists to handle any
 * "unset" templates that were already in the index before that skip was introduced.
 * If they do appear, remap them to kind "newsletter" so they group and render correctly.
 */
export function remapUnsetTemplates(results: SearchResult[]): SearchResult[] {
  return results.map((r) =>
    r.kind === 'template' && r.other.template_type === 'unset'
      ? ({
          ...r,
          kind: 'newsletter' as const,
          other: {
            ...r.other,
            id: r.other.newsletter_id ?? r.other.id,
            sent: 'false',
          },
        } satisfies SearchResult)
      : r,
  );
}

export function groupSearchResults(results: SearchResult[]) {
  // We exclude 'template' and 'campaign' from the group type as they are split into sub-groups.
  // Templates are grouped by template_type, and campaigns split out 'triggered_broadcast' as its own group.
  // First iteration preserves API order
  const resultsByKind = results.reduce(
    (groups, item) => {
      const kind = item.kind;
      groups[kind] ??= [];
      groups[kind].push(item);
      return groups;
    },
    {} as Record<SearchResult['kind'], SearchResult[]>,
  );

  const entries: [GroupType, SearchResult[]][] = [];

  for (const [kind, items] of Object.entries(resultsByKind)) {
    if (kind === 'template') {
      // Second pass: split template group by template_type; order = first occurrence in template list.
      const { groupedByTemplateType, templateTypes } = groupTemplatesByType(items);

      for (const t of templateTypes) {
        if (t === 'unset') continue;
        entries.push([t, groupedByTemplateType[t]]);
      }
    } else if (kind === 'campaign') {
      // Split triggered_broadcast campaigns into their own group.
      const campaigns: SearchResult[] = [];
      const triggeredBroadcasts: SearchResult[] = [];

      for (const item of items) {
        if (item.kind === 'campaign' && item.other.campaign_type === 'triggered_broadcast') {
          triggeredBroadcasts.push(item);
        } else {
          campaigns.push(item);
        }
      }

      if (campaigns.length > 0) {
        entries.push(['campaign', campaigns]);
      }
      if (triggeredBroadcasts.length > 0) {
        entries.push(['triggered_broadcast', triggeredBroadcasts]);
      }
    } else {
      entries.push([kind as GroupType, items]);
    }
  }

  entries.sort(([a], [b]) => {
    if (a === 'navigation') return -1;
    if (b === 'navigation') return 1;
    return 0;
  });

  return entries;
}

/** Customer result with a client-side relevance score computed via cmdk's commandScore. */
export type ScoredResult = SearchResult & { clientScore: number };

/** Which data sources are active for the current search session. */
export type ActiveSources = {
  search: boolean;
  people: boolean;
  docs: boolean;
  navigation: boolean;
  integrations: boolean;
  adAudiences: boolean;
  forms: boolean;
  webhooks: boolean;
  cdpOut: boolean;
  dataOut: boolean;
  objectTypes: boolean;
};

export type ResultsMap = {
  [K in keyof ActiveSources as `${K}`]: K extends 'people' ? ScoredResult[] : SearchResult[];
};

// Sources that fire only after the user scrolls to the sentinel in 'all' mode.
// In their dedicated filter modes (activeCategory === key) they fire immediately.
export const DEFERRED_SOURCE_KEYS = new Set<keyof ActiveSources>([
  'docs',
  'integrations',
  'adAudiences',
  'forms',
  'webhooks',
  'cdpOut',
  'dataOut',
  'objectTypes',
]);

export function hasAnyDeferredSource(activeSources: ActiveSources): boolean {
  return [...DEFERRED_SOURCE_KEYS].some((key) => activeSources[key]);
}

const MULTI_RESULT_POSITIONAL_DECAY_TOP_SCORE = 0.99;

/**
 * Scores a customer result against the current query.
 *
 * Scores against both title (name → fallback) and email separately, then takes the max.
 * This handles email-format queries where the customer has a name set — the title would
 * score near-zero against an email query, but the email attribute scores correctly.
 */
export function scoreCustomerResult(result: CustomerResult, query: string): number {
  const titleScore = defaultFilter(result.title, query);
  const emailScore = result.other.email != null ? defaultFilter(result.other.email, query) : 0;
  return Math.max(titleScore, emailScore);
}

/**
 * Merges Vertex search results and scored customer results into a single ranked list.
 *
 * Vertex results use relevance_score when present; fall back to positional decay
 * for results where Vertex does not return a score. A single fallback result keeps
 * score 1.0; multi-result fallbacks start at 0.99 so exact-match customers can rank first.
 * Customer results use their pre-computed clientScore.
 *
 * Stable sort descending — Vertex items are listed first in the combined array so they
 * win on equal scores, preserving Vertex's implicit ranking within each bucket.
 */
export function mergeByScoredInterleave(vertexResults: SearchResult[], peopleResults: ScoredResult[]): SearchResult[] {
  const total = vertexResults.length;

  const scored = [
    ...vertexResults.map((result, index) => ({
      result,
      score: result.relevance_score ?? getPositionalDecayScore(index, total),
    })),
    ...peopleResults.map((result) => ({
      result,
      score: result.clientScore,
    })),
  ];

  scored.sort((a, b) => b.score - a.score);

  return scored.map(({ result }) => result);
}

function getPositionalDecayScore(index: number, total: number): number {
  if (total <= 0) return 0;
  if (total === 1) return 1;
  return Math.max(0, MULTI_RESULT_POSITIONAL_DECAY_TOP_SCORE - index / total);
}

function collectPeopleAndSearch(
  activeSources: Pick<ActiveSources, 'search' | 'people'>,
  results: Pick<ResultsMap, 'search' | 'people'>,
): SearchResult[] {
  if (activeSources.search && activeSources.people) {
    return mergeByScoredInterleave(results.search, results.people);
  }
  if (activeSources.search) return [...results.search];
  if (activeSources.people) return [...results.people];
  return [];
}

/** Assembles all active API results into a single ordered list. */
export function buildAllApiResults(activeSources: ActiveSources, results: ResultsMap): SearchResult[] {
  const output = collectPeopleAndSearch(activeSources, results);
  if (activeSources.docs) output.push(...results.docs);
  if (activeSources.navigation) output.push(...results.navigation);
  if (activeSources.integrations) output.push(...results.integrations);
  if (activeSources.adAudiences) output.push(...results.adAudiences);
  if (activeSources.forms) output.push(...results.forms);
  if (activeSources.webhooks) output.push(...results.webhooks);
  if (activeSources.cdpOut) output.push(...results.cdpOut);
  if (activeSources.dataOut) output.push(...results.dataOut);
  if (activeSources.objectTypes) output.push(...results.objectTypes);
  return output;
}

// Chosen to block scattered false positives (e.g. "campai" → dark mode scores ~0.0001)
// while still passing abbreviation-style queries (e.g. "drk" → dark mode scores ~0.15).
// Uses cmdk's defaultFilter (commandScore) — https://github.com/dip/cmdk/blob/v1.1.1/cmdk/src/command-score.ts
const LOCAL_COMMAND_SCORE_THRESHOLD = 0.1;

export function matchesLocalCommand(query: string): (cmd: LocalCommand) => boolean {
  const trimmedQuery = query.trim();
  return (cmd) => {
    if (trimmedQuery.length === 0) return true;
    const haystack = [cmd.label, cmd.description].filter(Boolean).join(' ');
    return defaultFilter(haystack, trimmedQuery, cmd.keywords ?? []) >= LOCAL_COMMAND_SCORE_THRESHOLD;
  };
}
